import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { fromEvent, tap, throttleTime } from 'rxjs';

import { TranslateService } from '../../services/translate.service';

@Component({
  selector: 'app-carousel-flat',
  templateUrl: './carousel-flat.component.html',
  styleUrls: ['./carousel-flat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarouselFlatComponent implements OnChanges, AfterViewInit {
  @Input() viewAllTitle: string | undefined = undefined;
  @Input() heading: string | undefined = undefined;
  @Input() items: any[];
  @Input() itemsPerSlide = 4;
  @Input() link: string;
  @Input() showIndicators: boolean = true;
  @Input() showPagination: boolean = true;

  @ViewChild('carousel', { read: ElementRef }) carouselElement: ElementRef;
  @ContentChild(TemplateRef, { static: true }) itemTemplate: TemplateRef<any>;

  public slideSets: any[] = [];
  public currentSlide = 0;

  get isFirstPage(): boolean {
    return this.currentSlide === 0;
  }

  get isLastPage(): boolean {
    return this.currentSlide === this.slideSets.length - 1;
  }

  get viewAllTitleAttr(): string {
    return `${TranslateService.translate('carousel-controls-view-all')} ${
      this.viewAllTitle ?? this.heading
    }`;
  }

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    fromEvent(this.carouselElement.nativeElement, 'scroll')
      .pipe(
        throttleTime(150),
        tap(() => this.onSliderScroll())
      )
      .subscribe();
  }

  ngOnChanges({ items, itemsPerSlide }: SimpleChanges): void {
    if (items || itemsPerSlide) {
      this.slideSets = this.items?.reduce((acc: any, item: any) => {
        if (!acc.length) {
          acc.push([]);
        }

        if (acc[acc.length - 1]?.length < this.itemsPerSlide) {
          acc[acc.length - 1].push(item);
        } else {
          acc[acc.length] = [item];
        }

        return acc;
      }, []);

      const lastSlideIndex: number = this.slideSets.length - 1;
      const lastSlide: any[] = this.slideSets[lastSlideIndex];

      if (lastSlide.length < this.itemsPerSlide) {
        this.slideSets[lastSlideIndex] = this.items.slice(
          -1 * this.itemsPerSlide
        );
      }

      this.reset();
    }
  }

  reset(): void {
    if (this.currentSlide) {
      this.slideTo(0);
      this.currentSlide = 0;
    }
  }

  onSliderScroll(): void {
    this.currentSlide = Math.round(
      this.carouselElement.nativeElement.scrollLeft /
        this.carouselElement.nativeElement.offsetWidth
    );
    this.cdr.detectChanges();
  }

  prev(): void {
    if (this.isFirstPage) {
      return;
    }

    this.slideTo(this.currentSlide - 1);
  }

  next(): void {
    if (this.isLastPage) {
      return;
    }

    this.slideTo(this.currentSlide + 1);
  }

  slideTo(index: number): void {
    this.carouselElement.nativeElement.children[index].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  }
}
